"use client";

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

const RELEASE_ENDPOINT = "/api/release";
const POLL_INTERVAL_MS = 60_000;
const DISMISSED_RELEASE_STORAGE_KEY = "cede:staleVersion:dismissedRelease";
const RELEASE_QUERY_KEY = ["staleVersionCheck", "release"] as const;

type ReleaseResponse = {
  release: string | null;
};

type UseStaleVersionCheckResult = {
  isStale: boolean;
  clientRelease: string | null;
  serverRelease: string | null;
  dismiss: () => void;
  reload: () => void;
};

function readDismissedRelease(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(DISMISSED_RELEASE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeDismissedRelease(release: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DISMISSED_RELEASE_STORAGE_KEY, release);
  } catch {
    // localStorage unavailable (private mode / quota) — banner reappears next session.
  }
}

/**
 * Detects when the loaded client bundle is older than the release currently
 * served by the Next.js server. Compares the build-time `NEXT_PUBLIC_RELEASE`
 * (inlined into the JS bundle) against `/api/release`, which reports the release
 * of the deployment serving the request.
 */
export function useStaleVersionCheck(): UseStaleVersionCheckResult {
  const clientRelease = process.env.NEXT_PUBLIC_RELEASE ?? null;
  const [dismissedRelease, setDismissedRelease] = useState<string | null>(() => readDismissedRelease());

  const { data } = useQuery({
    queryKey: RELEASE_QUERY_KEY,
    queryFn: async ({ signal }): Promise<ReleaseResponse> => {
      const response = await fetch(RELEASE_ENDPOINT, { cache: "no-store", signal });
      if (!response.ok) {
        throw new Error(`release endpoint returned ${response.status}`);
      }
      return (await response.json()) as ReleaseResponse;
    },
    enabled: Boolean(clientRelease),
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 0,
    retry: false,
  });

  const serverRelease = data?.release ?? null;

  const dismiss = () => {
    if (!serverRelease) return;
    writeDismissedRelease(serverRelease);
    setDismissedRelease(serverRelease);
  };

  const reload = () => {
    window.location.reload();
  };

  const isStale =
    clientRelease !== null &&
    serverRelease !== null &&
    serverRelease !== clientRelease &&
    dismissedRelease !== serverRelease;

  return { isStale, clientRelease, serverRelease, dismiss, reload };
}
